/**
 * Date Utility Functions
 * Handles all date operations consistently across the application
 * Prevents timezone issues and ensures proper date formatting
 */

export interface DateInput {
  date: string | Date | null | undefined;
  fallback?: string | null;
}

export interface DateValidationResult {
  isValid: boolean;
  error?: string;
  normalizedDate?: string | null;
}

/**
 * Normalizes a date input to a consistent YYYY-MM-DD format
 * Handles timezone issues by treating dates as local dates
 */
export function normalizeDate(input: DateInput): string | null {
  const { date, fallback = null } = input;
  
  // Handle null, undefined, or empty string
  if (!date || date === '' || date === 'null' || date === 'undefined') {
    return fallback;
  }
  
  try {
    let dateObj: Date;
    
    if (typeof date === 'string') {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // Parse the date string
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }
    
    // Format as YYYY-MM-DD in local timezone
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('Date normalization error:', error);
    return fallback;
  }
}

/**
 * Converts a date to ISO string for API submission
 * Ensures consistent format for backend processing
 */
export function toApiDate(input: DateInput): string | null {
  const normalized = normalizeDate(input);
  return normalized;
}

/**
 * Converts API date response to form-friendly format
 * Handles timezone issues by treating as local date
 */
export function fromApiDate(input: DateInput): string | null {
  const { date, fallback = null } = input;
  
  if (!date || date === '' || date === 'null' || date === 'undefined') {
    return fallback;
  }
  
  try {
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Parse the date and convert to local YYYY-MM-DD
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }
    
    // Use local timezone to avoid date shifting
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('Date conversion error:', error);
    return fallback;
  }
}

/**
 * Validates a date string
 */
export function validateDate(input: string | null | undefined): DateValidationResult {
  if (!input || input === '') {
    return { isValid: true, normalizedDate: null };
  }
  
  try {
    const normalized = normalizeDate({ date: input });
    
    if (!normalized) {
      return { 
        isValid: false, 
        error: 'Invalid date format' 
      };
    }
    
    // Additional validation for reasonable date ranges
    const dateObj = new Date(normalized);
    const year = dateObj.getFullYear();
    const currentYear = new Date().getFullYear();
    
    if (year < 1900 || year > currentYear + 10) {
      return { 
        isValid: false, 
        error: 'Date is outside reasonable range' 
      };
    }
    
    return { 
      isValid: true, 
      normalizedDate: normalized 
    };
  } catch (error) {
    return { 
      isValid: false, 
      error: 'Invalid date format' 
    };
  }
}

/**
 * Validates date relationships (e.g., follow-up date after lead date)
 */
export function validateDateRelationship(
  earlierDate: string | null | undefined,
  laterDate: string | null | undefined,
  fieldName: string
): DateValidationResult {
  if (!earlierDate || !laterDate) {
    return { isValid: true };
  }
  
  const earlier = normalizeDate({ date: earlierDate });
  const later = normalizeDate({ date: laterDate });
  
  if (!earlier || !later) {
    return { isValid: true };
  }
  
  if (earlier > later) {
    return { 
      isValid: false, 
      error: `${fieldName} cannot be before the earlier date` 
    };
  }
  
  return { isValid: true };
}

/**
 * Gets today's date in YYYY-MM-DD format
 */
export function getTodayDate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats date for display
 */
export function formatDateForDisplay(date: string | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return '';
  }
}

/**
 * Checks if a date is in the past
 */
export function isDateInPast(date: string | null | undefined): boolean {
  if (!date) return false;
  
  const normalized = normalizeDate({ date });
  if (!normalized) return false;
  
  const dateObj = new Date(normalized);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return dateObj < today;
}

/**
 * Checks if a date is today
 */
export function isDateToday(date: string | null | undefined): boolean {
  if (!date) return false;
  
  const normalized = normalizeDate({ date });
  if (!normalized) return false;
  
  const dateObj = new Date(normalized);
  const today = new Date();
  
  return dateObj.toDateString() === today.toDateString();
}
